import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, gql } from '@apollo/client'
import { saveLoyaltyConfiguration } from '../../../apollo'
import { Box, Typography, Input, Button, Alert, Switch } from '@mui/material'
import useStyles from '../styles'
import useGlobalStyles from '../../../utils/globalStyles'

const SAVE_LOYALTY_CONFIGURATION = gql`
  ${saveLoyaltyConfiguration}
`

// Kept in step with the API's own defaults (models/configuration.js) and the
// apps' fallback tiers, so an unconfigured programme reads the same everywhere.
const DEFAULT_TIERS = [
  { minOrderAmount: 100, credits: 100 },
  { minOrderAmount: 50, credits: 50 },
  { minOrderAmount: 20, credits: 30 },
  { minOrderAmount: 0, credits: 10 }
]

// Credits customers earn on every order after their first one, and the rate
// those credits convert to wallet money at.
function LoyaltyConfiguration(props) {
  const { t } = props
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(true)
  const [creditsPerPound, setCreditsPerPound] = useState('')
  const [tiers, setTiers] = useState(DEFAULT_TIERS)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const [mutate, { loading }] = useMutation(SAVE_LOYALTY_CONFIGURATION)

  const classes = useStyles()
  const globalClasses = useGlobalStyles()

  useEffect(() => {
    setLoyaltyEnabled(props.loyaltyEnabled !== false)
    setCreditsPerPound(
      props.creditsPerPound === null ||
        typeof props.creditsPerPound === 'undefined'
        ? ''
        : String(props.creditsPerPound)
    )
    setTiers(
      props.loyaltyTiers?.length
        ? props.loyaltyTiers.map(tier => ({
            minOrderAmount: tier.minOrderAmount ?? 0,
            credits: tier.credits ?? 0
          }))
        : DEFAULT_TIERS
    )
  }, [props.loyaltyEnabled, props.creditsPerPound, props.loyaltyTiers])

  useEffect(() => {
    if (!successMessage) return
    const timeoutId = setTimeout(() => setSuccessMessage(''), 3000)
    return () => clearTimeout(timeoutId)
  }, [successMessage])

  useEffect(() => {
    if (!errorMessage) return
    const timeoutId = setTimeout(() => setErrorMessage(''), 3000)
    return () => clearTimeout(timeoutId)
  }, [errorMessage])

  const onTierChange = (index, field) => event => {
    const value = event.target.value
    setTiers(current =>
      current.map((tier, tierIndex) =>
        tierIndex === index
          ? { ...tier, [field]: value === '' ? '' : Math.max(0, Number(value)) }
          : tier
      )
    )
  }

  const onSave = e => {
    e.preventDefault()
    if (!Number(creditsPerPound)) {
      setErrorMessage(t('CreditsPerPoundRequired'))
      return
    }
    mutate({
      variables: {
        loyaltyEnabled,
        creditsPerPound: Number(creditsPerPound),
        loyaltyTiers: tiers.map(tier => ({
          minOrderAmount: Number(tier.minOrderAmount || 0),
          credits: Number(tier.credits || 0)
        }))
      },
      onCompleted: () => setSuccessMessage(t('Configuration saved')),
      onError: error => {
        setErrorMessage(t('An error occurred while saving configuration.'))
        console.error('Mutation error:', error)
      }
    })
  }

  return (
    <Box container className={classes.container}>
      <Box className={classes.flexRow}>
        <Box item className={classes.heading}>
          <Typography variant="h6" className={classes.text}>
            {t('LoyaltyProgramme')}
          </Typography>
        </Box>
        <Box ml={10} mt={1}>
          <label>{loyaltyEnabled ? t('Disable') : t('Enable')}</label>
          <Switch
            checked={loyaltyEnabled}
            onChange={e => setLoyaltyEnabled(e.target.checked)}
            style={{ color: 'black' }}
          />
        </Box>
      </Box>

      <Box className={classes.form}>
        <form>
          <Box>
            <Typography className={classes.labelText}>
              {t('CreditsPerPound')}
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-credits-per-pound"
              name="input-credits-per-pound"
              placeholder={t('CreditsPerPound')}
              type="number"
              min={1}
              value={creditsPerPound}
              onChange={e => {
                const value = e.target.value
                setCreditsPerPound(
                  value === '' ? '' : String(Math.max(0, Number(value)))
                )
              }}
              disableUnderline
              className={globalClasses.input}
            />
            <Typography className={classes.labelText}>
              {t('CreditsPerPoundHelp')}
            </Typography>
          </Box>

          <Typography className={classes.labelText}>
            {t('LoyaltyTiers')}
          </Typography>
          <Typography className={classes.labelText}>
            {t('LoyaltyTiersHelp')}
          </Typography>
          {tiers.map((tier, index) => (
            <Box
              key={index}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Box style={{ flex: 1 }}>
                <Typography className={classes.labelText}>
                  {t('TierOrderFrom')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id={`input-tier-min-${index}`}
                  type="number"
                  min={0}
                  value={tier.minOrderAmount}
                  onChange={onTierChange(index, 'minOrderAmount')}
                  disableUnderline
                  className={globalClasses.input}
                />
              </Box>
              <Box style={{ flex: 1 }}>
                <Typography className={classes.labelText}>
                  {t('TierCredits')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id={`input-tier-credits-${index}`}
                  type="number"
                  min={0}
                  value={tier.credits}
                  onChange={onTierChange(index, 'credits')}
                  disableUnderline
                  className={globalClasses.input}
                />
              </Box>
              {tiers.length > 1 ? (
                <Button
                  type="button"
                  onClick={() =>
                    setTiers(current =>
                      current.filter((item, itemIndex) => itemIndex !== index)
                    )
                  }
                  style={{ minWidth: 32 }}>
                  ×
                </Button>
              ) : null}
            </Box>
          ))}
          <Button
            type="button"
            onClick={() =>
              setTiers(current => [
                ...current,
                { minOrderAmount: 0, credits: 0 }
              ])
            }
            style={{ textTransform: 'none', fontSize: 13, marginLeft: '4%' }}>
            + {t('AddTier')}
          </Button>

          <Box>
            <Button
              className={globalClasses.button}
              disabled={loading}
              onClick={onSave}>
              {t('Save')}
            </Button>
          </Box>
          <Box mt={2}>
            {successMessage && (
              <Alert
                className={globalClasses.alertSuccess}
                variant="filled"
                severity="success">
                {successMessage}
              </Alert>
            )}
            {errorMessage && (
              <Alert
                className={globalClasses.alertError}
                variant="filled"
                severity="error">
                {errorMessage}
              </Alert>
            )}
          </Box>
        </form>
      </Box>
    </Box>
  )
}

export default withTranslation()(LoyaltyConfiguration)
